/**
 * Browser TTS helper. Uses the Web Speech API (SpeechSynthesis) to render
 * a typed line locally.
 *
 * Caveat the user should know about: SpeechSynthesis does NOT expose its
 * PCM output as a stream, so the typed line plays through the local
 * speakers only - it does NOT travel over the voice WS like the desktop
 * client's TTS does. The web client treats this as a "speak it to
 * yourself for confirmation" feature; a future server-side TTS endpoint
 * would unlock cross-client transmission.
 */

export function ttsAvailable(): boolean {
  return typeof window !== 'undefined'
    && typeof window.speechSynthesis !== 'undefined'
    && typeof window.SpeechSynthesisUtterance !== 'undefined'
}

export interface TtsSpeakOptions {
  readonly text: string
  readonly voice?: SpeechSynthesisVoice | null
  /** 0.1..10, default 1. Mirrors SpeechSynthesisUtterance.rate. */
  readonly rate?: number
  /** 0..1, default 1. */
  readonly volume?: number
}

/**
 * Speak the given text. Returns a promise that resolves when the utterance
 * ends or errors. Cancels any in-flight utterance first so a fast typist
 * doesn't queue up a backlog.
 */
export function speak(opts: TtsSpeakOptions): Promise<void> {
  if (!ttsAvailable()) {
    return Promise.reject(new Error('SpeechSynthesis is not available in this browser.'))
  }
  const text = opts.text.trim()
  if (text.length === 0) return Promise.resolve()

  return new Promise<void>((resolve, reject) => {
    const utter = new SpeechSynthesisUtterance(text)
    if (opts.voice !== undefined && opts.voice !== null) utter.voice = opts.voice
    utter.rate = opts.rate ?? 1
    utter.volume = opts.volume ?? 1
    utter.onend = (): void => resolve()
    utter.onerror = (ev: SpeechSynthesisErrorEvent): void => reject(new Error(ev.error))
    try {
      window.speechSynthesis.cancel()
    } catch { /* some browsers throw if there's nothing to cancel */ }
    window.speechSynthesis.speak(utter)
  })
}

/**
 * voiceslist resolves async on Chrome (the engine lazy-loads voices); the
 * page polls until at least one voice is reported, then caches. Returns
 * an empty array on Safari + Firefox if the engine hasn't initialised yet,
 * which is fine - callers should fall back to the default voice.
 */
export function listVoices(): ReadonlyArray<SpeechSynthesisVoice> {
  if (!ttsAvailable()) return []
  return window.speechSynthesis.getVoices()
}
